import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Settings, LogOut } from 'lucide-react';
import { VoicePanel } from './VoicePanel';

const statusOptions = [
  { value: 'online', label: 'Online', color: 'bg-green-500' },
  { value: 'idle', label: 'Idle', color: 'bg-yellow-500' },
  { value: 'dnd', label: 'Do Not Disturb', color: 'bg-red-500' },
  { value: 'offline', label: 'Invisible', color: 'bg-zinc-600' },
];

export const UserPanel = ({ onOpenSettings }) => {
  const { user, logout } = useAuth();
  const { updatePresence } = useSocket();
  const { t } = useTranslation();
  const [currentStatus, setCurrentStatus] = useState('online');

  if (!user) return null;

  const handleStatusChange = (status) => {
    setCurrentStatus(status);
    updatePresence(status);
  };

  const displayName = user.display_name || user.username;
  const statusColor = statusOptions.find(s => s.value === currentStatus)?.color || 'bg-green-500';

  return (
    <div className="flex flex-col flex-shrink-0" data-testid="user-panel-wrapper">
      <VoicePanel />
      <div className="h-[52px] px-2 flex items-center bg-[#08090c] border-t border-white/5" data-testid="user-panel">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 flex-1 min-w-0 px-1.5 py-1 rounded hover:bg-white/5 transition-colors" data-testid="user-panel-trigger">
            <div className="relative flex-shrink-0">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user.avatar} />
                <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-xs">{displayName[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#08090c] ${statusColor}`} />
            </div>
            <div className="min-w-0 flex-1 text-left">
              <p className="text-[13px] font-medium text-white truncate">{displayName}</p>
              <p className="text-[10px] text-zinc-500 truncate">{user.username}</p>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-[#12141a] border-white/10 text-white w-52" side="top" align="start">
          {/* Status options */}
          {statusOptions.map(s => (
            <DropdownMenuItem
              key={s.value}
              onClick={() => handleStatusChange(s.value)}
              className="text-sm focus:bg-white/5"
            >
              <div className={`h-2.5 w-2.5 rounded-full mr-2 ${s.color}`} />
              {s.label}
              {currentStatus === s.value && <span className="ml-auto text-cyan-400 text-xs">active</span>}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator className="bg-white/5" />
          <DropdownMenuItem onClick={onOpenSettings} className="text-sm focus:bg-white/5" data-testid="open-settings">
            <Settings className="h-4 w-4 mr-2 text-zinc-400" />
            {t('app.userSettings')}
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-white/5" />
          <DropdownMenuItem onClick={logout} className="text-sm text-red-400 focus:bg-red-500/10 focus:text-red-400" data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" />
            {t('app.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </div>
  );
};
